import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CombinedCheckoutModal from '../components/CombinedCheckoutModal';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinSession } from '../context/SkinSessionContext';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { goSalonHubForNewUser } from '../utils/salonHomeNav';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import '../styles/PageBackground.css';
import './AppointmentBookingPage.css';

const TIME_SLOTS = (() => {
  // 1-hour slots from 10:00 to 18:00
  const slots = [];
  for (let h = 10; h <= 18; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
  }
  return slots;
})();

const pad2 = (n) => String(n).padStart(2, '0');
const toLocalDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const isoToLocalParts = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: toLocalDateStr(d),
    slot: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
};

const addDaysToLocalDateStr = (dateStr, days) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return toLocalDateStr(d);
};

const localDateTimeToIso = (dateStr, slot) => {
  if (!dateStr || !slot) return null;
  // Construct local datetime (no timezone suffix) then convert to ISO.
  const dt = new Date(`${dateStr}T${slot}:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
};

const timeStrToMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const suggestSlotForToday = (slots) => {
  const now = new Date(); // user device time
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const parsed = (Array.isArray(slots) ? slots : [])
    .map((s) => ({ s, m: timeStrToMinutes(s) }))
    .filter((x) => typeof x.m === 'number')
    .sort((a, b) => a.m - b.m);
  if (parsed.length === 0) return '';
  // Pick the next upcoming slot (>= now). If none, return blank (force user to pick another day).
  const next = parsed.find((x) => x.m >= nowMinutes);
  return next ? next.s : '';
};

const AppointmentBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSkinSession();

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (
      location.state &&
      (location.state.treatmentCartItems != null ||
        location.state.cartItems != null ||
        location.state.appointmentDateTime != null ||
        location.state.appointmentDateTimes != null)
    ) {
      return location.state;
    }
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || location.state?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || location.state?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || location.state?.skinResults || null;
  const routineDataFromState = effectiveState?.routineData || location.state?.routineData || null;
  const aqiDataFromState = effectiveState?.aqiData ?? location.state?.aqiData ?? null;

  const treatmentCartItemsFromState =
    effectiveState?.treatmentCartItems ?? location.state?.treatmentCartItems ?? {};
  const productCartItemsFromState =
    effectiveState?.cartItems ?? location.state?.cartItems ?? {};

  const visibleConditionNames =
    effectiveState?.visibleConditionNames ?? location.state?.visibleConditionNames ?? null;

  const [treatmentCartItems] = useState(
    treatmentCartItemsFromState && typeof treatmentCartItemsFromState === 'object' ? treatmentCartItemsFromState : {}
  );
  const [productCartItems] = useState(
    productCartItemsFromState && typeof productCartItemsFromState === 'object' ? productCartItemsFromState : {}
  );

  const getSelectedTreatments = useCallback(() => {
    return Object.entries(treatmentCartItems || {})
      .filter(([, v]) => v && v.quantity > 0 && v.treatment)
      .map(([, v]) => ({ treatment: v.treatment, quantity: v.quantity }));
  }, [treatmentCartItems]);

  const requiredSessionCount = useMemo(() => {
    const qtys = Object.values(treatmentCartItems || {})
      .filter((v) => v && v.quantity > 0)
      .map((v) => Number(v.quantity) || 0);
    if (!qtys.length) return 0;
    return Math.max(...qtys, 1);
  }, [treatmentCartItems]);

  const initialAppointmentDateTimesIso = useMemo(() => {
    const fromState = effectiveState?.appointmentDateTimes ?? location.state?.appointmentDateTimes;
    if (Array.isArray(fromState) && fromState.length > 0) return fromState;
    const single = effectiveState?.appointmentDateTime ?? location.state?.appointmentDateTime;
    if (typeof single === 'string' && single.trim()) return [single];
    return [];
  }, [effectiveState, location.state]);

  const initialSessionsFromIso = useMemo(() => {
    const parts = [];
    initialAppointmentDateTimesIso.forEach((iso) => {
      const p = isoToLocalParts(iso);
      if (p) parts.push(p);
    });
    return parts;
  }, [initialAppointmentDateTimesIso]);

  const [appointmentSessions, setAppointmentSessions] = useState(() => {
    if (requiredSessionCount <= 0) return [];
    const now = new Date();
    const defaultDate = toLocalDateStr(now);
    const todaySuggestedSlot = suggestSlotForToday(TIME_SLOTS);
    const next = [];
    for (let i = 0; i < requiredSessionCount; i++) {
      const saved = initialSessionsFromIso[i];
      next.push({
        date: saved?.date || defaultDate,
        slot: saved?.slot || (i === 0 && (saved?.date || defaultDate) === defaultDate ? todaySuggestedSlot : ''),
      });
    }
    return next;
  });

  // Keep session fields in sync with the required session count.
  useEffect(() => {
    if (requiredSessionCount <= 0) {
      setAppointmentSessions([]);
      return;
    }
    setAppointmentSessions((prev) => {
      const now = new Date();
      const defaultDate = toLocalDateStr(now);
      const next = [...prev];
      while (next.length < requiredSessionCount) next.push({ date: defaultDate, slot: '' });
      while (next.length > requiredSessionCount) next.pop();

      // Ensure follow-up dates follow the "next session = +1 week" rule.
      const primaryDate = next[0]?.date;
      for (let i = 1; i < next.length; i++) {
        if (primaryDate) next[i] = { ...next[i], date: addDaysToLocalDateStr(primaryDate, i * 7) || next[i].date };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredSessionCount]);

  // When the user changes the primary appointment date, automatically shift follow-ups by +1 week each.
  useEffect(() => {
    if (requiredSessionCount <= 1) return;
    const primaryDate = appointmentSessions?.[0]?.date;
    if (!primaryDate) return;
    setAppointmentSessions((prev) => {
      const next = [...prev];
      for (let i = 1; i < next.length; i++) {
        const expected = addDaysToLocalDateStr(primaryDate, i * 7);
        if (expected && next[i]?.date !== expected) {
          next[i] = { ...next[i], date: expected };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentSessions?.[0]?.date, requiredSessionCount]);

  const [appointmentDateTimesIso, setAppointmentDateTimesIso] = useState(initialAppointmentDateTimesIso);

  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(() => !(initialAppointmentDateTimesIso && initialAppointmentDateTimesIso.length > 0));
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const hasBooked = appointmentDateTimesIso && appointmentDateTimesIso.length > 0;
  const autoSaveTimerRef = useRef(null);
  const lastAutoSavedRef = useRef('');

  useEffect(() => {
    if (!saveSuccess) return;
    const t = window.setTimeout(() => setSaveSuccess(false), 2500);
    return () => window.clearTimeout(t);
  }, [saveSuccess]);

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';

  const sharedFlowBaseState = useMemo(() => {
    return {
      capturedImage: capturedImageFromState,
      userData: userDataFromState,
      sessionId,
      skinResults: skinResultsFromState,
      routineData: routineDataFromState,
      aqiData: aqiDataFromState,
      treatmentCartItems,
      visibleConditionNames,
      cartItems: productCartItems,
      ...(token && { token }),
    };
  }, [
    capturedImageFromState,
    userDataFromState,
    sessionId,
    skinResultsFromState,
    routineDataFromState,
    aqiDataFromState,
    treatmentCartItems,
    visibleConditionNames,
    productCartItems,
    token,
  ]);

  const saveFlowState = useCallback(
    (extra) => {
      if (!sessionId) return;
      const current = getSkinFlowState(sessionId) || {};
      saveSkinFlowState(sessionId, {
        ...current,
        ...sharedFlowBaseState,
        sessionId,
        ...extra,
      });
    },
    [sessionId, sharedFlowBaseState]
  );

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems || {}).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const handleBackToTreatments = () => {
    navigate(pathWithSessionId('/treatment-recommendations', sessionId), {
      state:
        appointmentDateTimesIso && appointmentDateTimesIso.length > 0
          ? { ...sharedFlowBaseState, appointmentDateTimes: appointmentDateTimesIso, appointmentDateTime: appointmentDateTimesIso[0] }
          : sharedFlowBaseState,
    });
  };

  const handleHome = () => {
    goSalonHubForNewUser(navigate, {
      sessionId,
      token: token || null,
    });
  };

  const handleSaveAppointment = useCallback(async () => {
    setError(null);
    setSaveSuccess(false);
    if (!sessionId) {
      setError('Missing session. Please go back and try again.');
      return;
    }

    const selectedTreatments = getSelectedTreatments();
    if (!selectedTreatments.length) {
      setError('No treatments selected for booking.');
      return;
    }

    if (!requiredSessionCount || requiredSessionCount <= 0) {
      setError('Select treatment sessions quantity before booking an appointment.');
      return;
    }

    if (!Array.isArray(appointmentSessions) || appointmentSessions.length !== requiredSessionCount) {
      setError('Please complete all appointment slots.');
      return;
    }

    const isoList = appointmentSessions.map((s, idx) => {
      if (!s?.date || !s?.slot) return null;
      const iso = localDateTimeToIso(s.date, s.slot);
      if (!iso) return null;
      return iso;
    });

    if (isoList.some((x) => !x)) {
      setError('Please select a valid date/time slot for every session.');
      return;
    }

    try {
      setIsSaving(true);
      const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
      if (!baseUrl) throw new Error('API base URL missing');

      const res = await fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/save-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          ...(token && { app_session_token: token }),
          appointment_datetimes: isoList,
          selected_treatments: selectedTreatments,
        }),
        timeoutMs: 20000,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save appointment');
      }

      setAppointmentDateTimesIso(isoList);
      saveFlowState({ appointmentDateTimes: isoList, appointmentDateTime: isoList[0] });
      setSaveSuccess(true);
      setIsEditMode(false);
    } catch (e) {
      setError(e?.message || 'Failed to save appointment');
    } finally {
      setIsSaving(false);
    }
  }, [API_BASE_URL, appointmentSessions, getSelectedTreatments, requiredSessionCount, saveFlowState, sessionId, token]);

  // Auto-pick a slot from device time when primary date is today and no slot chosen yet.
  const primarySession = appointmentSessions?.[0] || null;
  const primaryDate = primarySession?.date || '';
  const primarySlot = primarySession?.slot || '';
  useEffect(() => {
    if (!isEditMode) return;
    if (!Array.isArray(appointmentSessions) || appointmentSessions.length === 0) return;
    const today = toLocalDateStr(new Date());
    if (!primaryDate || primaryDate !== today) return;
    if (primarySlot) return;
    const suggested = suggestSlotForToday(TIME_SLOTS);
    if (!suggested) return;
    setAppointmentSessions((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      if (prev[0]?.slot) return prev;
      const next = [...prev];
      next[0] = { ...next[0], slot: suggested };
      return next;
    });
  }, [isEditMode, appointmentSessions, primaryDate, primarySlot]);

  // Auto-save appointment when all required slots are filled (supports multi-treatment + multi-session).
  useEffect(() => {
    if (!isEditMode) return;
    if (!sessionId) return;
    if (!requiredSessionCount || requiredSessionCount <= 0) return;
    if (!Array.isArray(appointmentSessions) || appointmentSessions.length !== requiredSessionCount) return;
    if (isSaving) return;

    const selectedTreatments = getSelectedTreatments();
    if (!selectedTreatments.length) return;

    const isoList = appointmentSessions.map((s) => (s?.date && s?.slot ? localDateTimeToIso(s.date, s.slot) : null));
    if (isoList.some((x) => !x)) return;

    const key = JSON.stringify({ isoList, selectedTreatments });
    if (key === lastAutoSavedRef.current) return;

    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(async () => {
      // Re-check edit mode quickly before firing.
      if (!isEditMode) return;
      lastAutoSavedRef.current = key;
      await handleSaveAppointment();
    }, 450);

    return () => {
      if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    };
  }, [isEditMode, sessionId, requiredSessionCount, appointmentSessions, isSaving, getSelectedTreatments, handleSaveAppointment]);

  const handleContinueToProducts = () => {
    if (!appointmentDateTimesIso || appointmentDateTimesIso.length === 0) return;
    navigate(pathWithSessionId('/product-recommendations', sessionId), {
      state: { ...sharedFlowBaseState, appointmentDateTimes: appointmentDateTimesIso, appointmentDateTime: appointmentDateTimesIso[0] },
    });
  };

  const getTotalProductCartItems = () =>
    Object.values(productCartItems || {}).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalCombinedCartItems = () => getTotalTreatmentCartItems() + getTotalProductCartItems();
  const canCheckout = hasBooked && getTotalCombinedCartItems() > 0;

  const handleCheckout = () => {
    if (!hasBooked) return;
    setIsCheckoutModalOpen(true);
  };
  const handleCloseCheckoutModal = () => setIsCheckoutModalOpen(false);

  const handlePrimaryAction = async () => {
    if (hasBooked && !isEditMode) {
      setIsEditMode(true);
      return;
    }
    await handleSaveAppointment();
  };

  return (
    <div className="product-recommendations-page-shell page-bg appointment-booking-page-shell">
      <div className="product-recommendations-page-nav appointment-booking-page-nav">
        <button
          type="button"
          className="product-recommendations-page-back-button appointment-booking-page-back-button"
          onClick={handleBackToTreatments}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="product-recommendations-page-title-wrapper appointment-booking-page-title-wrapper">
          <SalonHeaderActions
            homeButtonClassName="product-recommendations-page-home-button appointment-booking-page-home-button"
            onHome={handleHome}
          />
          {canCheckout && (
            <button
              type="button"
              className="product-recommendations-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCombinedCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCombinedCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span className="product-recommendations-page-checkout-badge-icon">
                {getTotalCombinedCartItems()}
              </span>
            </button>
          )}
        </div>
      </div>

      <main className="product-recommendations-page-main appointment-booking-page-main">
        <div className="product-recommendations-page-container appointment-booking-page-container">
          <section className="appointment-booking-section">
            <h4 className="appointment-booking-section-title">Selected treatment sessions</h4>
            <div className="appointment-booking-selected-list">
              {Object.values(treatmentCartItems || {})
                .filter((v) => v && v.quantity > 0)
                .map((v, idx) => {
                  const t = v.treatment;
                  return (
                    <div key={t?.id || idx} className="appointment-booking-selected-row">
                      <span className="appointment-booking-selected-name">{t?.treatment || t?.name || 'Treatment'}</span>
                      <span className="appointment-booking-selected-qty">Qty: {v.quantity}</span>
                    </div>
                  );
                })}
              {getTotalTreatmentCartItems() <= 0 && (
                <div className="appointment-booking-hint">No treatments selected yet.</div>
              )}
            </div>
          </section>

          <section className="appointment-booking-section">
            <h4 className="appointment-booking-section-title">Choose date & time</h4>
            {appointmentSessions.map((s, idx) => {
              const sessionNum = idx + 1;
              const isFollowUp = sessionNum > 1;
              return (
                <div key={`appt_session_${idx}`} className="appointment-booking-session-block">
                  <div className="appointment-booking-session-title">
                    {isFollowUp ? `Follow-up session ${sessionNum}` : `Appointment session ${sessionNum}`}
                  </div>

                  <div className="appointment-booking-session-grid">
                    <div className="appointment-booking-field">
                      <label className="appointment-booking-label" htmlFor={`session-date-${idx}`}>
                        Date
                      </label>
                      <input
                        id={`session-date-${idx}`}
                        type="date"
                        className="appointment-booking-input"
                        value={s.date || ''}
                        disabled={!isEditMode}
                        onChange={(e) => {
                          const nextDate = e.target.value;
                          setAppointmentSessions((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], date: nextDate };
                            return next;
                          });
                        }}
                      />
                    </div>

                    <div className="appointment-booking-field">
                      <label className="appointment-booking-label" htmlFor={`session-slot-${idx}`}>
                        Time slot
                      </label>
                      <select
                        id={`session-slot-${idx}`}
                        className="appointment-booking-input"
                        value={s.slot || ''}
                        disabled={!isEditMode}
                        onChange={(e) => {
                          const nextSlot = e.target.value;
                          setAppointmentSessions((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], slot: nextSlot };
                            return next;
                          });
                        }}
                      >
                        <option value="">Select slot</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={`${idx}_${t}`} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="appointment-booking-primary-actions">
              <button
                type="button"
                className="skin-action-button appointment-booking-save-button"
                onClick={handlePrimaryAction}
                disabled={isSaving || requiredSessionCount <= 0}
              >
                {isSaving
                  ? 'Saving…'
                  : hasBooked && !isEditMode
                    ? 'Update appointment'
                    : hasBooked && isEditMode
                      ? 'Save updates'
                      : 'Book appointment'}
              </button>
              {canCheckout && (
                <button
                  type="button"
                  className="skin-action-button appointment-booking-checkout-inline"
                  onClick={handleCheckout}
                >
                  Checkout ({getTotalCombinedCartItems()})
                </button>
              )}
            </div>

            {error && <div className="appointment-booking-error">{error}</div>}
            {saveSuccess && (
              <div className="appointment-booking-success">Appointment booked successfully.</div>
            )}
          </section>

          {hasBooked && (
            <section className="appointment-booking-section">
              <h4 className="appointment-booking-section-title">Recommended products for your skin</h4>
              <p className="appointment-booking-engage-text">
                Your appointment is set. Now pick the best products for your most severe skin conditions to complete your routine.
              </p>
              <div className="appointment-booking-actions">
                <button
                  type="button"
                  className="skin-action-button appointment-booking-continue-button"
                  onClick={handleContinueToProducts}
                >
                  Explore recommended products
                </button>
                <button
                  type="button"
                  className="appointment-booking-secondary-button"
                  onClick={handleBackToTreatments}
                >
                  Back to treatments
                </button>
              </div>
            </section>
          )}
        </div>
      </main>

      <CombinedCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        productCartItemsMap={productCartItems}
        treatmentCartItemsMap={treatmentCartItems}
        onProductCartUpdate={null}
        onTreatmentCartUpdate={null}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
        appointmentDateTimes={appointmentDateTimesIso}
      />

    </div>
  );
};

export default AppointmentBookingPage;

